import React, { useState, Fragment, useEffect } from 'react';
import { Dialog, Transition, Listbox } from '@headlessui/react';
import { XMarkIcon, ChevronUpDownIcon, CalendarIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import { createScope, fetchConstructionData } from '../utils/api';
import { statusOptions } from '../utils/helpers';

// Month options generated for the dropdown
const generateMonthOptions = () => {
  return Array.from({ length: 24 }, (_, i) => {
    const date = new Date(2024, 3 + i, 1); // Starting April 2024
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  });
};

// Helper function to combine class names
function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function NewScopeModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    scope: '',
    subcontractor: '',
    point_of_contact: '',
    phone: '',
    status: 'Not Started On site',
    percentage_complete: 0,
    budget: '',
    start_month: 0, // Will be updated based on project settings
    duration_months: 3,
    distribution: 'even',
    notes: ''
  });

  const [selectedStatus, setSelectedStatus] = useState(statusOptions.find(s => s.name === 'Not Started On site'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [monthOptions] = useState(generateMonthOptions());
  const [loading, setLoading] = useState(true);
  
  // Fetch project settings when modal opens to get correct start month
  useEffect(() => {
    if (isOpen) {
      const fetchProjectSettings = async () => {
        try {
          setLoading(true);
          const data = await fetchConstructionData();
          
          // Extract project_start_date from the response
          if (data && data.project_start_date) {
            // Parse project start date to determine the correct start month index
            // Create date with local timezone handling to avoid UTC issues
            const projectDateParts = data.project_start_date.split('-').map(part => parseInt(part, 10));
            const projectDate = new Date(projectDateParts[0], projectDateParts[1] - 1, projectDateParts[2]);
            
            // Calculate months since April 2024 (our baseline in generateMonthOptions)
            const baselineDate = new Date(2024, 3, 1); // April 2024
            const monthsDiff = (projectDate.getFullYear() - baselineDate.getFullYear()) * 12 + 
                               (projectDate.getMonth() - baselineDate.getMonth());
            
            // Set start month to project start date if valid, otherwise keep default
            if (monthsDiff >= 0 && monthsDiff < monthOptions.length) {
              setFormData(prev => ({
                ...prev,
                start_month: monthsDiff
              }));
            }
          }
          setLoading(false);
        } catch (err) {
          console.error("Failed to fetch project settings:", err);
          setLoading(false);
        }
      };
      
      fetchProjectSettings();
    }
  }, [isOpen, monthOptions.length]);
  
  useEffect(() => {
    // Update form data when selected status changes
    if (selectedStatus) {
      setFormData(prev => ({ ...prev, status: selectedStatus.name }));
    }
  }, [selectedStatus]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'budget') {
      // Convert to number and handle empty string
      const budgetValue = value === '' ? '' : parseFloat(value);
      setFormData(prev => ({ ...prev, [name]: budgetValue }));
    } else if (name === 'percentage_complete') {
      // Ensure percentage is between 0 and 100
      const percentage = Math.max(0, Math.min(100, parseInt(value || 0, 10)));
      setFormData(prev => ({ ...prev, [name]: percentage }));
    } else if (name === 'start_month' || name === 'duration_months') {
      // Ensure these are integers
      const intValue = parseInt(value || 0, 10);
      setFormData(prev => ({ ...prev, [name]: intValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleMonthSelect = (index, fieldName) => {
    setFormData(prev => ({ ...prev, [fieldName]: index }));
  };
  
  const handlePercentageChange = (amount) => {
    // Adjust by increments of 5%
    const newValue = Math.max(0, Math.min(100, formData.percentage_complete + amount));
    setFormData(prev => ({ ...prev, percentage_complete: newValue }));
    
    // If percentage is 100%, set status to Complete
    if (newValue === 100) {
      setSelectedStatus(statusOptions.find(s => s.name === 'Complete'));
    } else if (newValue > 0 && formData.status === 'Not Started On site') {
      setSelectedStatus(statusOptions.find(s => s.name === 'On Going'));
    } else if (newValue === 0 && formData.status === 'On Going') {
      setSelectedStatus(statusOptions.find(s => s.name === 'Not Started On site'));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Convert budget to number for submission
      const submissionData = {
        ...formData,
        budget: formData.budget === '' ? 0 : parseFloat(formData.budget),
        percentage_complete: parseInt(formData.percentage_complete || 0, 10),
        start_month: parseInt(formData.start_month || 0, 10),
        duration_months: parseInt(formData.duration_months || 3, 10),
      };
      
      const result = await createScope(submissionData);
      setIsSubmitting(false);
      
      if (result.success) {
        // Reset form
        setFormData({
          scope: '',
          subcontractor: '',
          point_of_contact: '',
          phone: '',
          status: 'Not Started On site',
          percentage_complete: 0,
          budget: '',
          start_month: 0,
          duration_months: 3,
          distribution: 'even',
          notes: ''
        });
        setSelectedStatus(statusOptions.find(s => s.name === 'Not Started On site'));
        
        // Close modal and notify parent
        onSuccess(result);
        onClose();
      } else {
        setError(result.error || 'Failed to create scope');
      }
    } catch (err) {
      setIsSubmitting(false);
      setError(err.message || 'An error occurred while creating the scope');
      console.error('Error creating scope:', err);
    }
  };
  
  // Calculate end month index (for display)
  const endMonthIndex = Math.min(formData.start_month + formData.duration_months - 1, monthOptions.length - 1);
  
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-6 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                
                <div>
                  <div className="sm:mt-5">
                    <Dialog.Title as="h2" className="text-base/7 font-semibold text-gray-900 dark:text-gray-100">
                      Create New Budget Scope
                    </Dialog.Title>
                    <p className="mt-1 text-sm/6 text-gray-600 dark:text-gray-400">
                      Enter the details for the new budget scope item.
                    </p>
                    
                    {error && (
                      <div className="mt-2 rounded-md bg-red-50 dark:bg-red-900/50 p-4 text-sm text-red-700 dark:text-red-300">
                        {error}
                      </div>
                    )}
                    
                    <form onSubmit={handleSubmit} className="mt-10">
                      <div className="space-y-8">
                        {/* Scope Name (Required) */}
                        <div>
                          <label htmlFor="scope" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                            Scope Name *
                          </label>
                          <div className="mt-2">
                            <input
                              type="text"
                              name="scope"
                              id="scope"
                              required
                              placeholder="Enter scope name"
                              className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                              value={formData.scope}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                        
                        {/* Status Selector */}
                        <div>
                          <label className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                            Status
                          </label>
                          <Listbox value={selectedStatus} onChange={setSelectedStatus}>
                            <div className="relative mt-2">
                              <Listbox.Button 
                                className="grid w-full cursor-default grid-cols-1 rounded-md bg-white dark:bg-gray-700 py-1.5 pr-2 pl-3 text-left text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                              >
                                <span className="col-start-1 row-start-1 flex items-center gap-3 pr-6">
                                  <span
                                    className={classNames(
                                      selectedStatus.color,
                                      'inline-block px-2 py-0.5 text-xs font-semibold rounded-full'
                                    )}
                                  >
                                    {selectedStatus.name}
                                  </span>
                                </span>
                                <ChevronUpDownIcon
                                  aria-hidden="true"
                                  className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 dark:text-gray-400 sm:size-4"
                                />
                              </Listbox.Button>

                              <Listbox.Options
                                className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black/5 dark:ring-white/5 focus:outline-hidden data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 sm:text-sm"
                              >
                                {statusOptions.map((status) => (
                                  <Listbox.Option
                                    key={status.id}
                                    value={status}
                                    className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 dark:text-white select-none data-focus:bg-indigo-600 data-focus:text-white data-focus:outline-hidden"
                                  >
                                    <div className="flex items-center">
                                      <span
                                        className={classNames(
                                          status.color,
                                          'inline-block px-2 py-0.5 text-xs font-semibold rounded-full'
                                        )}
                                      >
                                        {status.name}
                                      </span>
                                    </div>
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </div>
                          </Listbox>
                        </div>
                        
                        {/* Percentage Complete with Progress Bar */}
                        <div>
                          <div className="flex justify-between items-center">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Completion Percentage
                            </label>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {formData.percentage_complete}%
                            </span>
                          </div>
                          <div className="mt-2">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handlePercentageChange(-5)}
                                className="p-1 rounded-full border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={formData.percentage_complete <= 0}
                              >
                                <MinusIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                              </button>
                              
                              <div className="flex-grow bg-gray-200 dark:bg-gray-600 rounded-full h-4">
                                <div 
                                  className="bg-indigo-600 h-4 rounded-full transition-all duration-200" 
                                  style={{ width: `${formData.percentage_complete}%` }}
                                />
                              </div>
                              
                              <button
                                type="button"
                                onClick={() => handlePercentageChange(5)}
                                className="p-1 rounded-full border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={formData.percentage_complete >= 100}
                              >
                                <PlusIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Subcontractor */}
                        <div>
                          <label htmlFor="subcontractor" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                            Subcontractor
                          </label>
                          <div className="mt-2">
                            <input
                              type="text"
                              name="subcontractor"
                              id="subcontractor"
                              placeholder="Enter subcontractor name"
                              className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                              value={formData.subcontractor}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                        
                        {/* Point of Contact and Phone */}
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                          <div>
                            <label htmlFor="point_of_contact" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                              Point of Contact
                            </label>
                            <div className="mt-2">
                              <input
                                type="text"
                                name="point_of_contact"
                                id="point_of_contact"
                                placeholder="Enter contact name"
                                className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                                value={formData.point_of_contact}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label htmlFor="phone" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                              Phone
                            </label>
                            <div className="mt-2">
                              <input
                                type="text"
                                name="phone"
                                id="phone"
                                placeholder="Enter phone number"
                                className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                                value={formData.phone}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Budget */}
                        <div>
                          <label htmlFor="budget" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                            Budget
                          </label>
                          <div className="mt-2">
                            <div className="relative">
                              <input
                                type="text"
                                name="budget"
                                id="budget"
                                placeholder="0.00"
                                className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 pl-8 pr-12 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                                value={formData.budget}
                                onChange={handleChange}
                              />
                              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                <span className="text-gray-500 dark:text-gray-400 sm:text-sm/6">$</span>
                              </div>
                              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                <span className="text-gray-500 dark:text-gray-400 sm:text-sm/6">USD</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Timeline */}
                        <div>
                          <label className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                            Timeline
                          </label>
                          <div className="mt-2 grid grid-cols-2 gap-4">
                            <div>
                              <label htmlFor="start_month" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                Start Month
                              </label>
                              <div className="relative">
                                <button
                                  type="button"
                                  className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6 flex items-center justify-between"
                                  onClick={() => {
                                    // Show dropdown UI - in a real app, this would control a state
                                    // For simplicity, we'll just show a scrollable dropdown
                                    // Note: This is a simplified version without a proper dropdown
                                  }}
                                >
                                  <span className="truncate">
                                    {monthOptions[formData.start_month] || 'Select month'}
                                  </span>
                                  <CalendarIcon className="ml-2 h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                                </button>
                                
                                {/* In a real implementation, this would be controlled by open/close state */}
                                <select
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                  value={formData.start_month}
                                  onChange={(e) => handleMonthSelect(parseInt(e.target.value, 10), 'start_month')}
                                >
                                  {monthOptions.map((month, index) => (
                                    <option key={index} value={index}>
                                      {month}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            
                            <div>
                              <label htmlFor="end_month" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                End Month
                              </label>
                              <div className="relative">
                                <button
                                  type="button"
                                  className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6 flex items-center justify-between"
                                  onClick={() => {
                                    // Show dropdown UI (simplified)
                                  }}
                                >
                                  <span className="truncate">
                                    {monthOptions[endMonthIndex] || 'Select month'}
                                  </span>
                                  <CalendarIcon className="ml-2 h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                                </button>
                                
                                {/* Simplified select dropdown for the end month */}
                                <select
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                  value={endMonthIndex}
                                  onChange={(e) => {
                                    const newEndMonth = parseInt(e.target.value, 10);
                                    const newDuration = newEndMonth - formData.start_month + 1;
                                    setFormData(prev => ({ ...prev, duration_months: Math.max(1, newDuration) }));
                                  }}
                                >
                                  {monthOptions.map((month, index) => (
                                    <option 
                                      key={index} 
                                      value={index}
                                      disabled={index < formData.start_month}
                                    >
                                      {month}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            {monthOptions[formData.start_month] === monthOptions[endMonthIndex] ? (
                              <span>1 month duration</span>
                            ) : (
                              <span>{formData.duration_months} months duration</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Notes */}
                        <div>
                          <label htmlFor="notes" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                            Notes
                          </label>
                          <div className="mt-2">
                            <textarea
                              id="notes"
                              name="notes"
                              rows={3}
                              placeholder="Enter any additional notes"
                              className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                              value={formData.notes}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-8 flex items-center justify-end gap-x-6">
                        <button
                          type="button"
                          onClick={onClose}
                          className="text-sm/6 font-semibold text-gray-900 dark:text-gray-100"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting || !formData.scope}
                          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? 'Creating...' : 'Create Scope'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}